import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { KioskShopCategoryListBlockConfig, UnitValue } from '@front-platform/content-builder';
import {
  kioskShopCategoryListBlockResolveConfig,
  normalizeUnitValue,
  serializeUnitValue,
} from '@front-platform/content-builder';
import { KIOSK_SHOP_CATEGORY_LIST_BLOCK_COMPONENT } from '@core/services/cb2-preview/cb2-preview-bloc-types';
import { previewScenarioData } from '@core/services/cb2-preview/preview-scenario-data.util';
import { previewScenarioForBloc$ } from '@core/services/cb2-preview/preview-scenario-for-bloc.util';
import type { KioskShopCategoryListBlockScenarioContent } from '@front-platform/content-builder';
import {
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import dayjs from 'dayjs';
import { TranslateService } from '@ngx-translate/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, from, of, skipWhile, Subject, timer, Observable } from 'rxjs';
import { debounceTime, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import { MarketplaceKeys, ShopKeys } from '@config/keys/shop.keys';
import { ProductKeys } from '@config/keys/product.keys';
import { CategoryKeys } from '@config/keys/category.keys';

import { DateUtils } from '@shared/utils/date/date.utils';
import { DoodCategoryWithProduct } from '@shared/interfaces/category.interface';

import { CartStoreRefiner } from '@common/refiners/cart.refiner';
import { ShopStoreRefiner } from '@common/refiners/shop.refiner';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';

import { ISimpleItem } from '@core/models/simple-item.model';
import { DoodShopModel, IShopCatalog, IShopCatalogTimeRule } from '@core/models/shop.model';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';

import { OrderStoreDispatcher } from '@common/dispatchers/order.dispatcher';
import { NativeService } from '@core/services/native/native.service';
import { ShopAvailableAtService } from '@core/services/shops/shop-available-at.service';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import {
  EligiblePromotionsLabelsService,
  mergePromotionLabelsIntoProducts,
} from '@core/services/eligible-promotions-labels/eligible-promotions-labels.service';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
export interface KioskShopCategoryListBlockRuntimeConfig {
  'display_back_shop_list_button_mobile'?: boolean;
  'display_back_shop_list_button_desktop'?: boolean;
  'back_shop_list_button_text'?: string;
  'shop_list_url'?: string;
  'shop_list_button_color'?: IContentBuilderFieldColor | undefined;
  'shop_list_button_text_color'?: IContentBuilderFieldColor | undefined;
  'category_list_top'?: number;
  'display_burger_menu_categories'?: boolean;
}

export type KioskShopCategoryListBlockConfigInput = Partial<
  Omit<KioskShopCategoryListBlockConfig, keyof KioskShopCategoryListBlockRuntimeConfig> &
    KioskShopCategoryListBlockRuntimeConfig
>;
export type KioskShopCategoryListBlockResolvedConfig = Omit<
  KioskShopCategoryListBlockConfig,
  keyof KioskShopCategoryListBlockRuntimeConfig
> &
  Partial<KioskShopCategoryListBlockRuntimeConfig>;
export interface KioskShopCategoryListBlockContract extends ConfigComponentContract<
  KioskShopCategoryListBlockConfigInput,
  KioskShopCategoryListBlockResolvedConfig
> {}

@Component({
  selector: 'app-kiosk-shop-category-list-block',
  templateUrl: './kiosk-shop-category-list-block.component.html',
  standalone: false,
})
export class KioskShopCategoryListBlockComponent
  implements OnInit, OnDestroy, KioskShopCategoryListBlockContract
{
  readonly config = input<KioskShopCategoryListBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskShopCategoryListBlockConfig,
    KioskShopCategoryListBlockRuntimeConfig
  >(kioskShopCategoryListBlockResolveConfig, () => ({ config: this.config() }));
  @HostBinding('class') classes =
    'flex h-full max-h-full min-h-0 w-full flex-col w-[20vw] min-w-[20vw] max-w-[20vw] overflow-visible';
  get displayBackShopListButtonMobile(): boolean {
    return this.resolvedConfig()['display_back_shop_list_button_mobile'] as unknown as boolean;
  }
  get displayBackShopListButtonDesktop(): boolean {
    return this.resolvedConfig()['display_back_shop_list_button_desktop'] as unknown as boolean;
  }
  get backShopListButtonText(): string {
    return this.resolvedConfig()['back_shop_list_button_text'] as unknown as string;
  }
  get shopListUrl(): string {
    return this.resolvedConfig()['shop_list_url'] as unknown as string;
  }
  get shopListButtonColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['shop_list_button_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get shopListButtonTextColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['shop_list_button_text_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get categoryListTop(): number {
    return this.resolvedConfig()['category_list_top'] as unknown as number;
  }
  get displayBurgerMenuCategories(): boolean {
    return this.resolvedConfig()['display_burger_menu_categories'] as unknown as boolean;
  }
  get backgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor;
  }
  get itemBackgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['item_background_color'] as unknown as IContentBuilderFieldColor;
  }
  get itemSelectedBackgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()[
      'item_selected_background_color'
    ] as unknown as IContentBuilderFieldColor;
  }
  get itemTextColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['item_text_color'] as unknown as IContentBuilderFieldColor;
  }
  get itemSelectedTextColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()[
      'item_selected_text_color'
    ] as unknown as IContentBuilderFieldColor;
  }
  get accentColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['accent_color'] as unknown as IContentBuilderFieldColor;
  }
  get displayCategoryName(): boolean {
    return this.resolvedConfig()['display_category_name'] as unknown as boolean;
  }
  get categoryImageRatio(): string {
    return this.resolvedConfig()['category_image_ratio'] as unknown as string;
  }
  get categoryCardPadding(): boolean {
    return this.resolvedConfig()['category_card_padding'] as unknown as boolean;
  }
  get categoryCardGap(): UnitValue {
    return this.resolvedConfig()['category_card_gap'] as unknown as UnitValue;
  }
  get categoryCardBorderRadius(): UnitValue {
    return this.resolvedConfig()['category_card_border_radius'] as unknown as UnitValue;
  }
  /** Saved blocs without this prop stay at 0px. */
  get categoryCardBorderRadiusCss(): string {
    return serializeUnitValue(
      normalizeUnitValue(this.categoryCardBorderRadius, { unit: 'px', value: 0 }),
    );
  }
  get categoryCardsPosition(): string {
    return this.resolvedConfig()['category_cards_position'] as unknown as string;
  }
  get displayArrows(): boolean {
    return this.resolvedConfig()['display_arrows'] as unknown as boolean;
  }
  get displayDots(): boolean {
    return this.resolvedConfig()['display_dots'] as unknown as boolean;
  }
  get displaySelectedCategoryBorder(): boolean {
    return this.resolvedConfig()['display_selected_category_border'] as unknown as boolean;
  }
  get selectedCategoryBorderColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()[
      'selected_category_border_color'
    ] as unknown as IContentBuilderFieldColor;
  }
  get unselectedCategoryBorderColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()[
      'unselected_category_border_color'
    ] as unknown as IContentBuilderFieldColor;
  }
  get selectedCategoryBorderWidth(): UnitValue {
    return this.resolvedConfig()['selected_category_border_width'] as unknown as UnitValue;
  }
  get selectedCategoryShadowSpread(): UnitValue {
    return this.resolvedConfig()['selected_category_shadow_spread'] as unknown as UnitValue;
  }
  get displaySelectedCategoryHalo(): boolean {
    return this.resolvedConfig()['display_selected_category_halo'] as unknown as boolean;
  }
  get selectedCategoryHaloColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()[
      'selected_category_halo_color'
    ] as unknown as IContentBuilderFieldColor;
  }
  get selectedCategoryHaloOpacity(): number {
    return this.resolvedConfig()['selected_category_halo_opacity'] as unknown as number;
  }
  get displaySelectedCategoryZoom(): boolean {
    return this.resolvedConfig()['display_selected_category_zoom'] as unknown as boolean;
  }
  get selectedCategoryZoomScale(): UnitValue {
    return this.resolvedConfig()['selected_category_zoom_scale'] as unknown as UnitValue;
  }

  @ViewChild('categoryTabsContainer') categoryTabsContainer?: ElementRef;
  @ViewChildren('itemElement') itemElements!: QueryList<ElementRef>;

  shop$!: Observable<Partial<DoodShopModel> | null>;
  isShopLoading$!: Observable<boolean>;
  categoriesWithProducts$!: Observable<DoodCategoryWithProduct[]>;

  private readonly previewState = inject(Cb2PreviewStateService);
  private readonly previewScenario$ = previewScenarioForBloc$(
    KIOSK_SHOP_CATEGORY_LIST_BLOCK_COMPONENT,
  );

  private readonly liveCategoriesWithProducts$ = combineLatest({
    categories: this.shopRefiner.selectActiveFlattenProductsWithCategories,
    promotionLabelsMap: this.eligiblePromotionsLabelsService.getPromotionLabelsByProductId$(),
  }).pipe(
    map(({ categories, promotionLabelsMap }) =>
      categories.map(cat => ({
        ...cat,
        productsInformation: mergePromotionLabelsIntoProducts(
          cat.productsInformation ?? [],
          promotionLabelsMap,
        ),
      })),
    ),
  );

  isOrderingAllowed = true;
  productKeys = ProductKeys;

  cartProducts$ = this.cartRefiner.selectActiveCartProducts;

  tab?: ISimpleItem;
  previewSelectedCategoryId: string | null = null;
  tabCategories!: ISimpleItem[];
  idsCategories!: string[];
  categoryKeys = CategoryKeys;
  isNotched = false;
  private destroyed$ = new Subject<boolean>();
  availableAtMessage: string | null = null;
  shouldBeAvailableNow = false;

  private previousScrollPosition = 0;

  isScrollingDown = false;

  warningMessages: string[] = [];

  isBurgerNavigationOpen = false;

  constructor(
    private readonly router: Router,
    private shopRefiner: ShopStoreRefiner,
    private cartRefiner: CartStoreRefiner,
    private shopSelector: ShopStoreSelector,
    private readonly eligiblePromotionsLabelsService: EligiblePromotionsLabelsService,
    private readonly orderDispatcher: OrderStoreDispatcher,
    private readonly activatedRoute: ActivatedRoute,
    protected settingsSelector: SettingsStoreSelector,
    private readonly routerHelper: RouterHelperService,
    private marketplaceSelector: MarketplaceStoreSelector,
    private readonly translateService: TranslateService,
    private readonly shopAvailableAtService: ShopAvailableAtService,
    private readonly analyticsService: AnalyticsService,
  ) {
    if (this.previewState.isActive()) {
      const previewVm$ = this.previewScenario$.pipe(
        map(scenario =>
          previewScenarioData<KioskShopCategoryListBlockScenarioContent>(
            scenario,
            KIOSK_SHOP_CATEGORY_LIST_BLOCK_COMPONENT,
          ),
        ),
        map(
          scenario =>
            scenario ?? {
              shop: null,
              isShopLoading: true,
              categoriesWithProducts: [],
              warningMessages: [],
              selectedCategoryId: null,
            },
        ),
      );

      this.shop$ = previewVm$.pipe(map(vm => vm.shop));
      this.isShopLoading$ = previewVm$.pipe(map(vm => vm.isShopLoading));
      this.categoriesWithProducts$ = previewVm$.pipe(
        map(vm => vm.categoriesWithProducts as unknown as DoodCategoryWithProduct[]),
      );

      previewVm$.pipe(takeUntilDestroyed()).subscribe(vm => {
        this.warningMessages = [...vm.warningMessages];
        this.populateCategoriesForTabs(
          vm.categoriesWithProducts as unknown as DoodCategoryWithProduct[],
        );
        this.previewSelectedCategoryId = vm.selectedCategoryId ?? null;
        if (this.previewSelectedCategoryId) {
          this.changeTab(this.previewSelectedCategoryId);
        } else {
          this.tab = undefined;
        }
      });
      return;
    }

    this.shop$ = this.shopSelector.selectActive$;
    this.isShopLoading$ = this.shopSelector.selectIsLoading$;
    this.categoriesWithProducts$ = this.liveCategoriesWithProducts$ as Observable<
      DoodCategoryWithProduct[]
    >;
  }

  ngOnInit(): void {
    if (this.previewState.isActive()) {
      return;
    }

    const marketplace = this.marketplaceSelector.marketplace;

    this.cartRefiner.selectActiveCartProducts
      .pipe(
        take(1),
        switchMap(products =>
          products.length ? this.orderDispatcher.checkCartIsValid$() : of(false),
        ),
      )
      .subscribe();

    this.shop$
      .pipe(
        takeUntil(this.destroyed$),
        skipWhile(shop => !shop),
        debounceTime(250),
        map(shop => {
          this.isOrderingAllowed = marketplace.available && !!shop?.[ShopKeys.Available];
          if (!marketplace.available) {
            this.analyticsService.trackEvent('warning', {
              warning_reason: 'marketplace_not_available',
              shop_id: shop?.[ShopKeys.Id],
              shop_name: shop?.[ShopKeys.Name],
              shop_available: shop?.[ShopKeys.Available],
              shop_available_at: shop?.[ShopKeys.AvailableAt],
              marketplace_id: marketplace[MarketplaceKeys.Id],
              marketplace_name: marketplace[MarketplaceKeys.Name],
              marketplace_available: marketplace[MarketplaceKeys.Available],
            });
          }
          if (!shop?.[ShopKeys.Available]) {
            this.analyticsService.trackEvent('warning', {
              warning_reason: 'shop_not_available',
              shop_id: shop?.[ShopKeys.Id],
              shop_name: shop?.[ShopKeys.Name],
              shop_available: shop?.[ShopKeys.Available],
              shop_available_at: shop?.[ShopKeys.AvailableAt],
              marketplace_id: marketplace[MarketplaceKeys.Id],
              marketplace_name: marketplace[MarketplaceKeys.Name],
              marketplace_available: marketplace[MarketplaceKeys.Available],
            });
          }
        }),
      )
      .subscribe();

    timer(1, 1000)
      .pipe(
        takeUntil(this.destroyed$),
        switchMap(() => this.shop$),
        tap(shop => {
          if (!shop) {
            return;
          }
          if (!shop.available_at) {
            return;
          }

          const now = DateUtils.dayjsInMarketplaceTimezone();
          const dateTimeAvailableAt = DateUtils.dayjsInMarketplaceTimezone(shop.available_at);
          this.shouldBeAvailableNow =
            dateTimeAvailableAt.isBefore(now) || dateTimeAvailableAt.isSame(now);
        }),
      )
      .subscribe();

    this.categoriesWithProducts$
      .pipe(
        filter(categories => !!categories.length),
        takeUntil(this.destroyed$),
        tap(categories => {
          this.populateCategoriesForTabs(categories);
        }),
      )
      .subscribe();

    from(NativeService.isNotched())
      .pipe(
        takeUntil(this.destroyed$),
        switchMap(isNotched => {
          this.isNotched = isNotched;
          if (this.isNotched) {
            return from(StatusBar.setStyle({ style: Style.Dark }));
          }
          return of(null);
        }),
      )
      .subscribe();

    timer(1, 1000)
      .pipe(
        takeUntil(this.destroyed$),
        tap(() => this.updateAvailableAtMessage()),
      )
      .subscribe();

    this.getWarningMessage();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  navigateTo(categoryId: string): void {
    this.shopSelector.selectActive$
      .pipe(
        take(1),
        map(shop => {
          if (!shop) {
            return;
          }
          this.router.navigate([`categories/${categoryId}`], {
            relativeTo: this.activatedRoute,
          });
        }),
      )
      .subscribe();
  }

  changeTab(id: string): void {
    this.tab = this.tabCategories?.find(tab => tab.value === id);
  }

  onCategoryClick(categoryId: string): void {
    this.navigateTo(categoryId);
  }

  backShopListButton(url: string): void {
    this.router.navigate([this.routerHelper.translateRoute(`/${url}`)]);
  }

  @HostListener('window:scroll')
  onScroll(): void {
    const currentScrollPosition =
      window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

    if (currentScrollPosition - this.previousScrollPosition >= 10) {
      this.isScrollingDown = true;
      return;
    }

    this.isScrollingDown = false;
    this.previousScrollPosition = currentScrollPosition;
  }

  toggleBurgerMenuNavigation(event: boolean): void {
    this.isBurgerNavigationOpen = event;
  }

  private populateCategoriesForTabs(categories: DoodCategoryWithProduct[]): void {
    this.tabCategories = categories
      .filter(
        category =>
          (category.level === 0 || category.level === undefined) &&
          (!!category.productsInformation?.length || !!category.hasDescendantWithVisibleProducts),
      )
      .map(category => {
        return {
          label: category.name,
          value: category.id,
          icon: category.icon,
        };
      });
    this.idsCategories = categories.map(category => {
      return category.id;
    });
  }

  private updateAvailableAtMessage(): void {
    this.shopSelector.selectActive$
      .pipe(
        take(1),
        tap(shop => {
          if (!shop) {
            return;
          }
          this.availableAtMessage = this.shopAvailableAtService.getAvailableAtDisplayString(shop);
        }),
      )
      .subscribe();
  }

  reloadPage(): void {
    document.location.reload();
  }

  private getWarningMessage(): void {
    combineLatest([
      this.isShopLoading$,
      this.shopRefiner.selectActiveShopEnabledDistributionMode,
      this.shopSelector.selectActive$,
      this.shopSelector.selectActive$.pipe(map(shop => shop?.catalogs)),
      this.shopRefiner.selectActiveShopEnabledCatalogs,
      this.shopRefiner.selectActiveShopEnabledStores,
      this.shopRefiner.selectActiveProductsCategories,
    ])
      .pipe(takeUntil(this.destroyed$), debounceTime(50))
      .subscribe(
        ([isShopLoading, distributionModes, shop, catalogs, enabledCatalogs, stores, products]) => {
          this.warningMessages = [];

          if (isShopLoading) {
            return;
          }

          if (!distributionModes?.length) {
            this.addWarningMessage(
              'shop-page.warning-message.distribution-mode-not-available',
              shop,
            );
            return;
          }

          if (!catalogs?.length) {
            this.addWarningMessage('shop-page.warning-message.catalog-not-available', shop);
            return;
          }

          const enabledCatalogsTodayTimeRules = this.getEnabledCatalogsTodayTimeRules(
            catalogs as IShopCatalog[],
          );

          if (catalogs?.length && !enabledCatalogsTodayTimeRules?.length) {
            this.addWarningMessage('shop-page.warning-message.catalog-not-available-today', shop);
          }

          const closestFutureCatalogTimeRule = this.findClosestFutureDate(
            enabledCatalogsTodayTimeRules,
          );

          if (closestFutureCatalogTimeRule && !enabledCatalogsTodayTimeRules?.length) {
            this.addWarningMessage('shop-page.warning-message.catalog-not-available-before', shop, {
              time: dayjs()
                .startOf('day')
                .add(closestFutureCatalogTimeRule, 'second')
                .format('HH:mm'),
            });
            return;
          }

          if (enabledCatalogs?.length && !stores?.length) {
            this.addWarningMessage('shop-page.warning-message.stores-not-available', shop);
          }

          if (!products?.length) {
            this.addWarningMessage('shop-page.warning-message.products-not-available', shop);
          }
        },
      );
  }

  private addWarningMessage(
    translationKey: string,
    shop: DoodShopModel | null,
    params?: {
      [key: string]: string;
    },
  ): void {
    this.warningMessages.push(
      `${this.translateService.instant(translationKey)} ${params?.time ? params.time : ''}`,
    );

    this.analyticsService.trackEvent('warning', {
      warning_reason: 'no_product_displayed_on_shop_page',
      no_product_available_reason: translationKey.split('.').pop(),
      shop_id: shop?.[ShopKeys.Id],
      shop_name: shop?.[ShopKeys.Name],
      shop_available: shop?.[ShopKeys.Available],
      shop_available_at: shop?.[ShopKeys.AvailableAt],
    });
  }

  private getEnabledCatalogsTodayTimeRules(enabledCatalogs: IShopCatalog[] | undefined): number[] {
    return (enabledCatalogs || [])
      .reduce((acc, catalog) => {
        if (catalog?.enabled_time_rules) {
          acc.push(...catalog.enabled_time_rules);
        }
        return acc;
      }, [] as IShopCatalogTimeRule[])
      .filter(timeRule => timeRule.days_of_week.includes(DateUtils.isoDayOfWeek()))
      .map(timeRule => timeRule.start_time);
  }

  private findClosestFutureDate(dates: number[]): number | null {
    const now = dayjs();

    const dayjsDates = dates.map(seconds => dayjs().startOf('day').add(seconds, 'second'));
    const futureDates = dayjsDates.filter(date => date.isAfter(now));
    const closestFutureDate = futureDates.reduce((closestDate, currentDate) => {
      return currentDate.isBefore(closestDate) ? currentDate : closestDate;
    }, futureDates[0]);
    const closestFutureDateInSeconds = closestFutureDate?.diff(dayjs().startOf('day'), 'second');
    return closestFutureDateInSeconds || null;
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskShopCategoryListBlockComponent, {
  'keyAliases': {},
});
